import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { IonSpinner } from '@ionic/react'

import { Button } from '@components/atoms/Button'
import { DateTimePicker } from '@components/atoms/DateTimePicker'
import { InputField } from '@components/atoms/InputField'
import { RequiredMark } from '@components/atoms/RequiredMark'
import { Select } from '@/components/molecules/Select'

import { ContestCategory } from '@enums'

interface IContestEditForm {
  onSubmit: (data) => Promise<void>
  isLoading: boolean
}

const contestCategoryOptions = Object.keys(ContestCategory).map(
  (category: string) => ({
    value: category.toUpperCase(),
    label: ContestCategory[category],
  })
)

const getVideoOptionsLabel = (timeInSeconds: number) => {
  if (timeInSeconds >= 60) return `${timeInSeconds / 60}min`

  return `${timeInSeconds}s`
}

const ContestVideoOptions = [5, 10, 15, 30, 45, 60, 120, 180, 240, 300].map(
  (duration: number) => ({
    value: `${duration}`,
    label: getVideoOptionsLabel(duration),
  })
)

export const ContestEditForm: FC<IContestEditForm> = ({
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation('common')

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      contestName: '',
      contestDescription: '',
      goesOnline: null,
      contestCategory: '',
      contestVideoLength: '',
    },
    mode: 'all',
  })

  return (
    <form className="mb-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8">
        <Controller
          control={control}
          name="contestName"
          rules={{ minLength: 2, maxLength: 60, required: true }}
          render={({ field }) => (
            <InputField
              name="contestName"
              textAlign="right"
              label={t('contest.create.name')}
              value={field.value}
              role="presentation"
              onChange={(e) => field.onChange(e.value)}
              labelSuffix={<RequiredMark />}
              isDefault
            />
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="contestCategory"
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              label={t('contest.create.type')}
              value={field.value}
              options={contestCategoryOptions}
              labelSuffix={<RequiredMark />}
              onChange={(e) => field.onChange(e.value)}
              name="contestCategory"
            />
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="contestDescription"
          rules={{ minLength: 2, maxLength: 200, required: true }}
          render={({ field }) => (
            <InputField
              textAlign="right"
              label={
                <Trans i18nKey="contest.create.description">
                  {t('contest.create.description')}
                </Trans>
              }
              value={field.value}
              role="presentation"
              autocomplete="off"
              isTextarea
              labelSuffix={<RequiredMark />}
              onChange={(e) => field.onChange(e.value)}
              isDefault
              name="contestDescription"
            />
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="goesOnline"
          rules={{ required: true }}
          render={({ field }) => (
            <DateTimePicker
              label={t('contest.create.online')}
              value={field.value}
              labelSuffix={<RequiredMark />}
              onChange={(e) => field.onChange(e.value)}
            />
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="contestVideoLength"
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              label={t('contest.create.video')}
              value={field.value}
              options={ContestVideoOptions}
              labelSuffix={<RequiredMark />}
              onChange={(e) => field.onChange(e.value)}
              name="contestVideoLength"
            />
          )}
        />
      </div>
      <div className="text-center">
        <Button
          size="large"
          isDisabled={isLoading || !isValid}
          className="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {!isLoading ? (
            t('Select Video')
          ) : (
            <IonSpinner
              name="dots"
              slot="icon-only"
              className={'text-quaternary'}
            />
          )}
        </Button>
      </div>
    </form>
  )
}
